import {
    showSuccessMessage,
    showErrorMessage,
} from "../../models/store/storeActions";
import { PlannedWorkShiftSaveDto } from "../../models/plannedWorkShift/plannedWorkShift";
import { savePlannedWorkShift } from "../../services/plannedWorkShiftService";
import { ApiError } from "../../services/baseService";
import { reloadPlannedWorkShifts } from "../../store/workShiftPlanningSlice";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { useTranslation } from "react-i18next";
import { RecurrenceAction } from "../../models/common/enums";
import {
    createRecurringShiftInstance,
    editRecurringShift,
} from "../../services/recurringShiftService";
import { Base } from "../../framework/base";
import { RecurringShiftInstanceType } from "../../models/recurringShift/recurringShift";

const getSaveForRecurrenceAction = (
    data: PlannedWorkShiftSaveDto,
    recurrenceAction: RecurrenceAction,
    shift: PlannedWorkShiftSaveDto
) => {
    // No recurrence-action specified,
    if (!recurrenceAction) {
        return savePlannedWorkShift(data);
    }

    const recurringId = data?.recurrence?.recurringShiftId;
    if (!recurringId) return;

    if (recurrenceAction === RecurrenceAction.OnlyThis) {
        // Edit this planned shift only if instance already overridden or generated
        if (
            shift.isOverridenFromRecurrence ||
            shift.isGeneratedFromRecurrence
        ) {
            return savePlannedWorkShift(data);
        }

        return createRecurringShiftInstance({
            recurringShiftId: recurringId,
            recurringShiftRowId: data.recurrence.rowId,
            date: Base.dayjsToJsonDate(shift.startTime), // Use original shift startTime instead off updated.
            type: RecurringShiftInstanceType.Override,
            shift: data,
        });
    } else {
        return editRecurringShift(recurringId, {
            rowId: data.recurrence.rowId,
            startDate: data.recurrence.recurrenceStartDate,
            splitFromDate:
                recurrenceAction === RecurrenceAction.AllFuture
                    ? // Use original shift startTime instead off updated.
                    Base.dayjsToJsonDate(shift.startTime)
                    : null,
            startTime: data.startTime,
            endTime: data.endTime,
            label: data.label,
            comment: data.comment,
            intervalInWeeks:
                data.recurrence?.intervalInWeeks &&
                data.recurrence.intervalInWeeks > 0
                    ? data.recurrence.intervalInWeeks
                    : 1,
            repeatDays: data.recurrence?.repeatDays,
            endDate: data.recurrence?.recurrenceEndDate,
            employeeId: data.employeeId ?? undefined,
            vehicleId: data.vehicleId ?? undefined,
            routeId: data.routeId ?? undefined,
        });
    }
};

export const useSavePlannedWorkShiftWithFeedback = (
    shift: PlannedWorkShiftSaveDto,
    recurrenceAction: RecurrenceAction,
    onSuccess?: () => void,
    onFailure?: (error: any) => void
) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const showUnemployable = useAppSelector(
        state => state.workShiftPlanning.filters.showUnemployable
    );

    const submit = (data: PlannedWorkShiftSaveDto) => {
        getSaveForRecurrenceAction(data, recurrenceAction, shift)
            .then(() => {
                dispatch(showSuccessMessage(t("common.saveSuccess")));
                dispatch(reloadPlannedWorkShifts());

                if (!showUnemployable && !data.employeeId) {
                    dispatch(showSuccessMessage(t("workShiftPlanning.unassignedShiftsNotVisible")));
                }

                onSuccess?.();
            })
            .catch((error: ApiError) => {
                dispatch(
                    showErrorMessage(
                        error?.status === 409
                            ? t("common.saveConflict")
                            : t("common.saveFailed")
                    )
                );
                onFailure?.(error);
            });
    };

    return submit;
};
